import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import AccessRoute from './components/AccessRoute.jsx';
import AppShell from './layouts/AppShell.jsx';
import { menuLeaves, ROLES } from './config/menuData.js';
import { Unauthorized, Forbidden, NotFound, ServerError } from './pages/status/index.jsx';
import { LoginPage, RegisterPage } from './pages/login/index.jsx';
import UserAccountsRbacPage from './pages/user-accounts-rbac/index.jsx';
import HarborGeographyPage from './pages/harbor-geography/index.jsx';
import AssistanceAreasPage from './pages/assistance-areas/index.jsx';
import ServiceMaintenancePage from './pages/service-maintenance/index.jsx';
import OwnedTugsPage from './pages/owned-tugs/index.jsx';
import MaintenanceBunkeringPage from './pages/maintenance-bunkering/index.jsx';
import OpsWallboardPage from './pages/ops-wallboard/index.jsx';
import VesselRegistryPage from './pages/vessel-registry/index.jsx';
import CustomerRegistrationPage from './pages/customer-registration/index.jsx';
import CustomerApprovalsPage from './pages/customer-approvals/index.jsx';
import TariffRateCardsPage from './pages/tariff-rate-cards/index.jsx';
import AllocationRulesPage from './pages/allocation-rules/index.jsx';
import CustomerBookingPage from './pages/customer-booking/index.jsx';
import VesselMasterArrivalPage from './pages/vessel-master-arrival/index.jsx';
import CrewRosterPage from './pages/crew-roster/index.jsx';
import DispatchPlanningPage from './pages/dispatch-planning/index.jsx';
import CaptainPwaPage from './pages/captain-pwa/index.jsx';
import DailyTugReportPage from './pages/daily-tug-report/index.jsx';
import SofFinalizerPage from './pages/sof-finalizer/index.jsx';
import ClockCorrectionsPage from './pages/clock-corrections/index.jsx';
import InvoicingDraftsPage from './pages/invoicing-drafts/index.jsx';
import SapQueuePage from './pages/sap-queue/index.jsx';
import AffiliatePortalPage from './pages/affiliate-portal/index.jsx';
import AffiliateBroadcastsPage from './pages/affiliate-broadcasts/index.jsx';
import IntercompanyAuditPage from './pages/intercompany-audit/index.jsx';
import PilotCoordinatorPage from './pages/pilot-coordinator/index.jsx';
import LiveTrackingPage from './pages/live-tracking/index.jsx';
import FleetSchedulerPage from './pages/fleet-scheduler/index.jsx';
import FleetReportsPage from './pages/fleet-reports/index.jsx';
import CommercialDashboardPage from './pages/commercial-dashboard/index.jsx';
import FavoriteVesselsPage from './pages/favorite-vessels/index.jsx';
import OrderTriagingPage from './pages/order-triaging/index.jsx';
import IntegrationSettingsPage from './pages/integration-settings/index.jsx';
import AuditLedgerPage from './pages/audit-ledger/index.jsx';
import SaasBillingDeskPage from './pages/saas-billing-desk/index.jsx';
import DashboardPage from './pages/dashboard/index.jsx';
import ChangePasswordPage from './pages/change-password/index.jsx';
import './App.scss';

// Slugs wired to a real page component (others fall back to 404).
const IMPLEMENTED = {
  dashboard: true,
  'user-accounts-roles-permissions-rbac': true,
  'harbor-geography': true,
  'customer-approvals': true,
  'owned-tug-asset-database-maintenance-logs': true,
  'maintenance-bunkering-scheduler': true,
  'vessel-master-registry-port-call-log': true,
  'crew-roster-certifications-compliance': true,
  'dispatch-planning-resource-allocation': true,
  'tariff-rate-cards-version-manager': true,
  'allocation-rules-compulsory-floors': true,
  'execution-clock-corrections': true,
  'invoicing-draft-billing-approver': true,
  'sap-s4hana-integration-queue': true,
  'affiliate-portal-capacity-register': true,
  'affiliate-broadcasts-job-review': true,
  'affiliate-costs-intercompany-audit': true,
  'pilot-assignments-market-coordinator': true,
  'live-tracking-map-operations-console': true,
  'ops-wallboard': true,
  'fleet-scheduler-captain-gantt-timeline': true,
  'fleet-performance-operations-ppa-reports': true,
  'commercial-revenues-costs-dashboard': true,
  'order-triaging-assisted-bookings': true,
  'external-service-integrations-settings': true,
  'immutable-audit-trails-logger': true,
  'saas-provider-billing-desk-invoicing': true,
  'customer-booking-form-live-dashboard': true,
  'customer-favorite-vessels-preferences': true,
  'vessel-master-arrival': true,
  'captain-dispatch-ticket-execution-clock-pwa': true,
  'daily-tug-report': true,
  'statement-of-facts-finalizer-delay-logger': true,
};

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public auth + status routes — rendered bare (no app shell). */}
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route
              path="customer-registration-account-portal"
              element={<CustomerRegistrationPage />}
            />
            <Route path="401" element={<Unauthorized />} />
            <Route path="403" element={<Forbidden />} />
            <Route path="500" element={<ServerError />} />

            {/* Authenticated app — single responsive shell (viewport-based). */}
            <Route element={<AppShell />}>
              <Route index element={<Navigate to="/dashboard" replace />} />

              {/* Any authenticated user. */}
              <Route element={<AccessRoute roles={[]} />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="change-password" element={<ChangePasswordPage />} />
              </Route>

              {/* System Administrator — governance & configuration. */}
              <Route element={<AccessRoute roles={[ROLES.ADMIN]} />}>
                <Route path="user-accounts-roles-permissions-rbac" element={<UserAccountsRbacPage />} />
                <Route path="harbor-geography" element={<HarborGeographyPage />} />
                <Route path="assistance-areas" element={<AssistanceAreasPage />} />
                <Route path="service-maintenance" element={<ServiceMaintenancePage />} />
                <Route path="customer-approvals" element={<CustomerApprovalsPage />} />
                <Route path="external-service-integrations-settings" element={<IntegrationSettingsPage />} />
                <Route path="immutable-audit-trails-logger" element={<AuditLedgerPage />} />
                <Route path="saas-provider-billing-desk-invoicing" element={<SaasBillingDeskPage />} />
              </Route>

              {/* Operations Monitor / Dispatcher. */}
              <Route element={<AccessRoute roles={[ROLES.DISPATCHER]} />}>
                <Route path="owned-tug-asset-database-maintenance-logs" element={<OwnedTugsPage />} />
                <Route path="vessel-master-registry-port-call-log" element={<VesselRegistryPage />} />
                <Route path="crew-roster-certifications-compliance" element={<CrewRosterPage />} />
                <Route path="fleet-scheduler-captain-gantt-timeline" element={<FleetSchedulerPage />} />
                <Route path="dispatch-planning-resource-allocation" element={<DispatchPlanningPage />} />
                <Route path="live-tracking-map-operations-console" element={<LiveTrackingPage />} />
                <Route path="ops-wallboard" element={<OpsWallboardPage />} />
                <Route path="fleet-performance-operations-ppa-reports" element={<FleetReportsPage />} />
              </Route>

              {/* Maintenance & Bunkering Coordinator (write); Dispatcher read-only. */}
              <Route element={<AccessRoute roles={[ROLES.MAINT, ROLES.DISPATCHER]} />}>
                <Route path="maintenance-bunkering-scheduler" element={<MaintenanceBunkeringPage />} />
              </Route>

              {/* Shared operational pages. */}
              <Route element={<AccessRoute roles={[ROLES.DISPATCHER, ROLES.BOOKING]} />}>
                <Route path="order-triaging-assisted-bookings" element={<OrderTriagingPage />} />
              </Route>
              <Route element={<AccessRoute roles={[ROLES.DISPATCHER, ROLES.PILOT]} />}>
                <Route path="pilot-assignments-market-coordinator" element={<PilotCoordinatorPage />} />
              </Route>
              <Route element={<AccessRoute roles={[ROLES.DISPATCHER, ROLES.AFFILIATE]} />}>
                <Route path="affiliate-broadcasts-job-review" element={<AffiliateBroadcastsPage />} />
                <Route path="affiliate-portal-capacity-register" element={<AffiliatePortalPage />} />
              </Route>

              {/* Tug Captain — field PWA. */}
              <Route element={<AccessRoute roles={[ROLES.CAPTAIN]} />}>
                <Route path="captain-dispatch-ticket-execution-clock-pwa" element={<CaptainPwaPage />} />
                <Route path="statement-of-facts-finalizer-delay-logger" element={<SofFinalizerPage />} />
              </Route>
              {/* Daily Tug Report — deck section (captain) and engine section (chief engineer). */}
              <Route element={<AccessRoute roles={[ROLES.CAPTAIN, ROLES.ENGINEER]} />}>
                <Route path="daily-tug-report" element={<DailyTugReportPage />} />
              </Route>

              {/* Billing / Finance Officer. */}
              <Route element={<AccessRoute roles={[ROLES.FINANCE, ROLES.TARIFF]} />}>
                <Route path="invoicing-draft-billing-approver" element={<InvoicingDraftsPage />} />
              </Route>
              <Route element={<AccessRoute roles={[ROLES.FINANCE]} />}>
                <Route path="sap-s4hana-integration-queue" element={<SapQueuePage />} />
                <Route path="commercial-revenues-costs-dashboard" element={<CommercialDashboardPage />} />
                <Route path="affiliate-costs-intercompany-audit" element={<IntercompanyAuditPage />} />
              </Route>
              {/* Clock corrections: Finance and Operations (8927). */}
              <Route element={<AccessRoute roles={[ROLES.FINANCE, ROLES.DISPATCHER]} />}>
                <Route path="execution-clock-corrections" element={<ClockCorrectionsPage />} />
              </Route>

              {/* Pricing / Tariff Manager. */}
              <Route element={<AccessRoute roles={[ROLES.TARIFF]} />}>
                <Route path="tariff-rate-cards-version-manager" element={<TariffRateCardsPage />} />
                <Route path="allocation-rules-compulsory-floors" element={<AllocationRulesPage />} />
              </Route>

              {/* Customer self-service. */}
              <Route element={<AccessRoute roles={[ROLES.CUSTOMER, ROLES.BOOKING, ROLES.ADMIN, ROLES.DISPATCHER]} />}>
                <Route path="customer-booking-form-live-dashboard" element={<CustomerBookingPage />} />
              </Route>
              <Route element={<AccessRoute roles={[ROLES.CUSTOMER]} />}>
                <Route path="customer-favorite-vessels-preferences" element={<FavoriteVesselsPage />} />
                <Route path="vessel-master-arrival" element={<VesselMasterArrivalPage />} />
              </Route>

              {/* Remaining menu pages — 404 placeholder until built. */}
              {menuLeaves
                .filter((leaf) => !IMPLEMENTED[leaf.slug])
                .map((leaf) => (
                  <Route key={leaf.slug} path={leaf.slug} element={<NotFound />} />
                ))}

              {/* Unknown in-app route */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
